import { useState } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import DetailCardFields from '#client/components/DetailCardFields';
import FormStepModal from '#client/components/FormStepModal';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import { OWNER_CONTRIBUTIONS_URL, FAQ_OVERVIEW_URL } from '#client/constants/FAQs';
import { DISCLOSURES_TEXT } from '#client/constants/text';
import useBankingPath from '#client/hooks/useBankingPath';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useSessionStorage from '#client/hooks/useSessionStorage';

import AccountDetails from './AccountDetails';
import { DialogContentStyled, Wrapper } from './HelpAnotherWayModal.styles';
import StepItem from './StepItem';

const descriptionNode = ({ showText, hideText, show, onClick }) => (
  <Stack
    alignItems="center"
    direction="row"
    gap="4px"
    mt="8px"
    onClick={onClick}
    sx={{ cursor: 'pointer' }}
  >
    {show ? <VisibilityOffIcon color="text.blue" /> : <RemoveRedEyeIcon color="text.blue" />}
    <Text
      color="blue"
      size="md"
      weight="medium"
    >
      {show ? hideText : showText}
    </Text>
  </Stack>
);

const linkAnotherWebsiteSteps = [
  {
    number: 2,
    title: 'Log in to the other bank or app ',
    description: 'Transactions will happen there, not on your Banking dashboard.'
  },
  {
    number: 3,
    title: 'Search for how to link a bank account',
    description: 'Words to look for: transfers, send money, add bank, external accounts'
  },
  {
    number: 4,
    title: 'Add ZenBusiness Banking',
    description: 'When connecting, provide your account info.'
  },
  {
    number: 5,
    title: 'Follow all steps to be verified',
    description: 'Trial deposits may be used to verify your account. '
  }
];

const addZenToAnotherApp = [
  {
    number: 2,
    title: 'Log in to the other bank or app',
    description: 'Transactions will happen there, not on your Banking dashboard.'
  },
  {
    number: 3,
    title: 'Add ZenBusiness Banking',
    description: 'When connecting, provide your account info.'
  }
];

export default function HelpAnotherWayModal({ handleClose }) {
  const { isWebView } = WebView;
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const bankingPath = useBankingPath();
  const dashboardUrl = `${bankingPath}/dashboard`;
  const navigate = useNavigate();
  const [, setIsRedirectDashboardNotFunded] = useSessionStorage(
    `stop_redirect_dashboard_notfunded.${businessEntityUuid}`,
    false
  );
  const toggleShowAccountDetails = () => {
    setShowAccountDetails((prev) => !prev);
  };

  const toggleShowCardDetails = () => {
    setShowCardDetails((prev) => !prev);
  };

  const handleGoToBankDashboard = () => {
    setIsRedirectDashboardNotFunded(true);
    navigate(dashboardUrl);
    handleClose();
  };

  return (
    <FormStepModal
      disableBack
      handleClose={handleClose}
      isHideBackButton
      title="Link account"
    >
      <DialogContentStyled>
        <ContainerGrid>
          <Stack
            gap={{ xs: '16px', md: '24px' }}
            mb={{ xs: '32px', md: '48px' }}
            mt={{ xs: '40px', md: '48px' }}
          >
            <Heading
              align="center"
              family="secondary"
              size="3xl"
              weight="bold"
            >
              Deposit another way
            </Heading>
            <Text
              align="center"
              color="primary"
              size="lg"
            >
              Move money from another bank or financial app, like Venmo, PayPal, or Stripe.
            </Text>
          </Stack>
          <FocusGrid>
            <Wrapper>
              <Stack gap="24px">
                <Heading
                  align="center"
                  family="secondary"
                  size="2xl"
                  weight="bold"
                >
                  Link from another website or app
                </Heading>
                <Stack gap={{ xs: '16px', md: '32px' }}>
                  <StepItem
                    description={descriptionNode({
                      showText: 'Show account details',
                      hideText: 'Hide account details',
                      show: showAccountDetails,
                      onClick: toggleShowAccountDetails
                    })}
                    key={1}
                    number={1}
                    title="Copy ZenBusiness Banking account and routing numbers"
                  />
                  {showAccountDetails && (
                    <Stack
                      gap="2px"
                      marginLeft={{ xs: '48px', md: '56px' }}
                    >
                      <AccountDetails />
                    </Stack>
                  )}
                  {linkAnotherWebsiteSteps.map((step) => (
                    <StepItem
                      description={step.description}
                      key={step.number}
                      number={step.number}
                      title={step.title}
                    />
                  ))}
                </Stack>
              </Stack>

              <Stack
                gap="24px"
                mt="64px"
              >
                <Heading
                  align="center"
                  size="2xl"
                  weight="bold"
                >
                  Add ZenBusiness to another app
                </Heading>
                <Stack gap={{ xs: '16px', md: '32px' }}>
                  <StepItem
                    description={descriptionNode({
                      showText: 'Show card details',
                      hideText: 'Hide card details',
                      show: showCardDetails,
                      onClick: toggleShowCardDetails
                    })}
                    key={1}
                    number={1}
                    title="Copy your ZenBusiness Banking virtual card number"
                  />
                  {showCardDetails && (
                    <Stack
                      gap="16px"
                      marginLeft={{ xs: '48px', md: '56px' }}
                    >
                      <DetailCardFields />
                    </Stack>
                  )}
                  {addZenToAnotherApp.map((step) => (
                    <StepItem
                      description={step.description}
                      key={step.number}
                      number={step.number}
                      title={step.title}
                    />
                  ))}
                </Stack>
              </Stack>

              <Stack
                gap="16px"
                mt="64px"
                mx={{ xs: 0, md: '25%' }}
              >
                <Button
                  component="a"
                  href={FAQ_OVERVIEW_URL}
                  size="large"
                  target="_blank"
                  variant="contained"
                >
                  Get support
                </Button>
                <Button
                  onClick={handleGoToBankDashboard}
                  size="large"
                  variant="outlined"
                >
                  Go to bank dashboard
                </Button>
              </Stack>

              {!isWebView && (
                <Stack
                  mt={{ xs: '40px', md: '48px' }}
                  width="100%"
                >
                  <Text
                    align="center"
                    size="md"
                  >
                    Plan on using personal funds?{' '}
                    <Link
                      color="info"
                      href={OWNER_CONTRIBUTIONS_URL}
                      rel="noreferrer"
                      target="_blank"
                    >
                      See the benefits
                    </Link>
                  </Text>
                </Stack>
              )}

              <Stack mt="64px">
                <Text
                  align="left"
                  color="tertiary"
                  size="xs"
                  sx={{
                    lineHeight: '16px'
                  }}
                >
                  {DISCLOSURES_TEXT}
                </Text>
              </Stack>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
    </FormStepModal>
  );
}

HelpAnotherWayModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};
